import { guid } from '../../../../utils/guid';
import { formatDayMonth } from '../../../../utils/date-only';
import { normalizeAmount } from '../../../../utils/format-amount';
import { ORDER_STATUS_LABELS, OrderStatus } from '../../orders-new/models/order.dto';
import { ShipmentStatus } from '../../shipment/models/shipment.dto';
import { SHIPMENT_STATUS_LABELS } from '../../shipment/models/shipment.view-model';
import { AllocationDto, ClientOrderDto, ClientOrderItemDto, CreatedClientOrderItemDto, ShipmentLineDto } from './client-order.dto';

export interface ClientOrderVm {
  id: number;
  orderNumber: string;
  clientName: string;
  name: string | null;
  createdAt: string;
  deadline: string | null;
  confirmedAt: string | null;
  items: ClientOrderItemVm[];
}

export namespace ClientOrderVm {
  export function mapModelToViewModel(order: ClientOrderDto): ClientOrderVm {
    return {
      ...order,
      items: order.items.map(ClientOrderItemVm.mapModelToViewModel)
    };
  }
}

export interface ClientOrderItemVm {
  /**
   * Guid or another unique temp id for presentational purposes.
   */
  id: string;
  data: ClientOrderItemData;
  children: ClientOrderItemVm[];
}

export interface ClientOrderItemData {
  clientOrderItemId: number;
  bomItemId: number | null;
  name: string;
  /**
   * The snapshot's unit — what amount is counted in; printed after it (0069).
   */
  unit: string;
  /**
   * BOM-style amount: the ordered quantity on a position, per-one-parent on a child —
   * the same meaning as in the BOM tree, so the two trees read alike.
   */
  amount: number;
  /**
   * Pegging — on a position only (0 / [] on children). A position with any allocation
   * is frozen: production already copied its tree, so only the root amount stays editable.
   */
  allocatedAmount: number;
  allocations: AllocationVm[];
  /**
   * Shipping — the third link on a position, next to the allocation: what is on any
   * wysyłka (Planowana included), what already left, and the lines themselves.
   */
  inShipmentsAmount: number;
  shippedAmount: number;
  shipments: ShipmentLineVm[];
  /**
   * The position's thumbnail (0082) — frozen at creation, else the BOM item's current
   * photo; null when there is none, and on rows the create responses replaced.
   */
  primaryPhotoAttachmentId: number | null;
}

export interface ShipmentLineVm {
  shipmentItemId: number;
  shipmentId: number;
  shipmentNumber: string;
  transport: string;
  amount: number;
  unit: string;
  /**
   * A line of a Planowana wysyłka can still be taken off from here.
   */
  planned: boolean;
  statusLabel: string;
  /**
   * The shipped day once shipped, the planned day before that, null without either.
   */
  dateLabel: string | null;
}

export namespace ShipmentLineVm {
  export function mapModelToViewModel(line: ShipmentLineDto): ShipmentLineVm {
    return {
      shipmentItemId: line.shipmentItemId,
      shipmentId: line.shipmentId,
      shipmentNumber: line.shipmentNumber,
      transport: line.transport,
      amount: line.amount,
      unit: line.unit,
      planned: line.status === ShipmentStatus.planned,
      statusLabel: SHIPMENT_STATUS_LABELS[line.status],
      dateLabel: line.shippedAt
        ? formatDayMonth(line.shippedAt.slice(0, 10))
        : line.plannedDate ? formatDayMonth(line.plannedDate) : null
    };
  }
}

export interface AllocationVm {
  allocationId: number;
  amount: number;
  unit: string;
  orderId: number;
  orderNumber: string;
  statusLabel: string;
  /**
   * "12.10 – 24.10", or null while the zlecenie is not planned yet.
   */
  windowLabel: string | null;
  /**
   * The other zamówienia the same zlecenie covers — "obejmuje też ZK-xx" in the flow
   * view; empty when it covers this order alone.
   */
  alsoCovers: string[];
}

export namespace AllocationVm {
  export function mapModelToViewModel(allocation: AllocationDto): AllocationVm {
    return {
      allocationId: allocation.allocationId,
      amount: allocation.amount,
      unit: allocation.unit,
      orderId: allocation.orderId,
      orderNumber: allocation.orderNumber,
      statusLabel: ORDER_STATUS_LABELS[allocation.orderStatus],
      windowLabel: allocation.plannedStartDate && allocation.plannedEndDate
        ? `${formatDayMonth(allocation.plannedStartDate)} – ${formatDayMonth(allocation.plannedEndDate)}`
        : null,
      alsoCovers: allocation.alsoCovers
    };
  }
}

export function isFrozen(position: ClientOrderItemVm): boolean {
  return position.data.allocations.length > 0;
}

export function remainingAmount(position: ClientOrderItemVm): number {
  return normalizeAmount(position.data.amount - position.data.allocatedAmount);
}

/**
 * The name "Utwórz zlecenie" gives the zlecenie (0114): the first product, ` (+N)` for
 * the further ones, then the client — `Przyczepa P-4 (+2) – Agro-Mex`, the seeder's
 * convention. Null without positions, so the numbered default stays.
 */
export function productionOrderName(positions: ClientOrderItemVm[], clientName: string): string | null {
  if (positions.length === 0) {
    return null;
  }

  const further = positions.length > 1 ? ` (+${positions.length - 1})` : '';

  return `${positions[0].data.name}${further} – ${clientName}`;
}

// the shipping twin of remainingAmount: what no wysyłka, planned or shipped, carries yet
export function unshippedAmount(position: ClientOrderItemVm): number {
  return normalizeAmount(position.data.amount - position.data.inShipmentsAmount);
}

/**
 * Replaces a position's shipment lines and keeps the two sums in step with them.
 */
export function withShipmentLines(position: ClientOrderItemVm, shipments: ShipmentLineVm[]): ClientOrderItemVm {
  return {
    ...position,
    data: {
      ...position.data,
      shipments,
      inShipmentsAmount: normalizeAmount(shipments.reduce((sum, x) => sum + x.amount, 0)),
      shippedAmount: normalizeAmount(shipments.filter(x => !x.planned).reduce((sum, x) => sum + x.amount, 0))
    }
  };
}

export namespace ClientOrderItemVm {
  export function mapModelToViewModel(item: ClientOrderItemDto): ClientOrderItemVm {
    return {
      id: guid(),
      data: {
        clientOrderItemId: item.clientOrderItemId,
        bomItemId: item.bomItemId,
        name: item.name,
        unit: item.unit,
        amount: item.perParentAmount ?? item.amount,
        allocatedAmount: item.allocatedAmount,
        allocations: item.allocations.map(AllocationVm.mapModelToViewModel),
        inShipmentsAmount: item.inShipmentsAmount,
        shippedAmount: item.shippedAmount,
        shipments: item.shipments.map(ShipmentLineVm.mapModelToViewModel),
        primaryPhotoAttachmentId: item.primaryPhotoAttachmentId ?? null
      },
      children: item.children.map(mapModelToViewModel)
    };
  }

  // a just-created node: the response's snapshot over the empty pegging and shipping a new node has
  export function mapCreatedToViewModel(item: CreatedClientOrderItemDto): ClientOrderItemVm {
    return {
      id: guid(),
      data: {
        clientOrderItemId: item.clientOrderItemId,
        bomItemId: item.bomItemId,
        name: item.name,
        unit: item.unit,
        amount: item.perParentAmount ?? item.amount,
        allocatedAmount: 0,
        allocations: [],
        inShipmentsAmount: 0,
        shippedAmount: 0,
        shipments: [],
        primaryPhotoAttachmentId: null
      },
      children: item.children.map(mapCreatedToViewModel)
    };
  }
}

// --- tree updates: pure functions over one position, applied to every root by the store (bom-tree-item.view-model precedent) ---

export function updateNode(nodeId: string, update: (node: ClientOrderItemVm) => ClientOrderItemVm): (root: ClientOrderItemVm) => ClientOrderItemVm {
  return root => {
    if (root.id === nodeId) {
      return update(root);
    }

    return {
      ...root,
      children: root.children.map(updateNode(nodeId, update))
    };
  };
}

export function addChild(nodeId: string, newChild: ClientOrderItemVm): (root: ClientOrderItemVm) => ClientOrderItemVm {
  return root => {
    if (root.id === nodeId) {
      return {
        ...root,
        children: [...root.children, newChild]
      };
    }

    return {
      ...root,
      children: root.children.map(addChild(nodeId, newChild))
    };
  };
}

export function replaceNode(nodeId: string, newNode: ClientOrderItemVm): (root: ClientOrderItemVm) => ClientOrderItemVm {
  return root => {
    if (root.id === nodeId) {
      return newNode;
    }

    return {
      ...root,
      children: root.children.map(replaceNode(nodeId, newNode))
    };
  };
}

export function removeNode(nodeId: string): (root: ClientOrderItemVm) => ClientOrderItemVm {
  return root => {
    const newChildren = root.children.filter(child => child.id !== nodeId);

    if (root.children.length !== newChildren.length) {
      return {
        ...root,
        children: newChildren
      };
    }

    return {
      ...root,
      children: root.children.map(removeNode(nodeId))
    };
  };
}

export function findNode(roots: ClientOrderItemVm[], nodeId: string): ClientOrderItemVm | undefined {
  for (const root of roots) {
    if (root.id === nodeId) {
      return root;
    }

    const found = findNode(root.children, nodeId);

    if (found) {
      return found;
    }
  }

  return undefined;
}
